/**
 * Hearts, top left.
 *
 * Discrete hearts rather than a continuous bar: "two hits left" is a decision
 * a player can make mid-fight, where "sixty-one percent" is not. Only touched
 * when a value actually changes.
 */

/** Health per heart. Damage numbers are tuned in these units. */
export const HEALTH_PER_HEART = 4;

export class HealthBar {
  private readonly root: HTMLDivElement;
  private lastSignature = '';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'health-bar';
    this.root.setAttribute('role', 'status');
    container.appendChild(this.root);
  }

  update(current: number, max: number): void {
    const signature = `${current}/${max}`;
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;

    const hearts = Math.ceil(max / HEALTH_PER_HEART);
    this.root.replaceChildren();

    for (let i = 0; i < hearts; i++) {
      const filled = Math.max(0, Math.min(HEALTH_PER_HEART, current - i * HEALTH_PER_HEART));
      // Two glyphs, not one: an empty outline that never moves, and a red
      // heart clipped to the fraction left. A single glyph tinted by fill
      // makes a half heart look like a different colour of full heart.
      const heart = document.createElement('span');
      heart.className = 'heart';
      heart.style.setProperty('--fill', String(filled / HEALTH_PER_HEART));

      const empty = document.createElement('span');
      empty.className = 'heart-empty';
      empty.textContent = '♥';

      const full = document.createElement('span');
      full.className = 'heart-full';
      full.textContent = '♥';

      heart.append(empty, full);
      this.root.appendChild(heart);
    }

    this.root.setAttribute('aria-label', `Health ${current} of ${max}`);
  }

  destroy(): void {
    this.root.remove();
  }
}
